import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import {
  SuccessNotification,
  RejectedNotification,
} from 'src/notifications/SuccessNotification'
import Sentry from 'src/utils/sentry'

// Async thunks for unified allergen operations
export const bulkUpdateAllergenAssignments = createAsyncThunk(
  'unifiedAllergen/bulkUpdateAllergenAssignments',
  async ({ venueId, assignments }, { rejectWithValue }) => {
    try {
      const response = await API.post(
        'api2',
        `venues/${venueId}/allergens/bulk-update`,
        {
          body: { assignments },
        },
      )

      SuccessNotification({
        message: `Successfully updated ${assignments.length} allergen assignments`,
      })

      return response
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update allergen assignments'
      RejectedNotification({ message })
      Sentry.captureException(error)
      return rejectWithValue(message)
    }
  },
)

export const getAllergenAssignments = createAsyncThunk(
  'unifiedAllergen/getAllergenAssignments',
  async ({ venueId, options = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })

      const response = await API.get(
        'api2',
        `venues/${venueId}/allergens?${params.toString()}`,
      )
      return response
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch allergen assignments'
      Sentry.captureException(error)
      return rejectWithValue(message)
    }
  },
)

export const getItemAllergenAssignments = createAsyncThunk(
  'unifiedAllergen/getItemAllergenAssignments',
  async ({ venueId, itemType, itemId }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        'api2',
        `venues/${venueId}/allergens/${itemType}/${itemId}`,
      )
      return response
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch item allergen assignments'
      Sentry.captureException(error)
      return rejectWithValue(message)
    }
  },
)

export const deleteItemAllergenAssignments = createAsyncThunk(
  'unifiedAllergen/deleteItemAllergenAssignments',
  async ({ venueId, itemType, itemId }, { rejectWithValue }) => {
    try {
      const response = await API.delete(
        'api2',
        `venues/${venueId}/allergens/${itemType}/${itemId}`,
      )

      SuccessNotification({
        message: 'Allergen assignments deleted successfully',
      })

      return response
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete allergen assignments'
      RejectedNotification({ message })
      Sentry.captureException(error)
      return rejectWithValue(message)
    }
  },
)

const initialState = {
  assignments: [],
  pagination: {
    page: 1,
    limit: 100,
    total: 0,
    pages: 0,
  },
  isLoading: false,
  error: null,
  lastUpdated: null,
  realTimeEnabled: false,
}

const unifiedAllergenSlice = createSlice({
  name: 'unifiedAllergen',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    setLastUpdated: (state, action) => {
      state.lastUpdated = action.payload
    },
    enableRealTime: state => {
      state.realTimeEnabled = true
    },
    disableRealTime: state => {
      state.realTimeEnabled = false
    },
    addRealTimeUpdate: (state, action) => {
      const { itemType, itemId, allergen, value } = action.payload

      // Find and update existing assignment
      const existingIndex = state.assignments.findIndex(
        assignment =>
          assignment.itemType === itemType &&
          assignment.itemId === itemId &&
          assignment.allergen === allergen,
      )

      if (existingIndex !== -1) {
        state.assignments[existingIndex].value = value
        state.assignments[existingIndex].updatedAt = new Date().toISOString()
      } else {
        // Add new assignment
        state.assignments.push({
          itemType,
          itemId,
          allergen,
          value,
          updatedAt: new Date().toISOString(),
        })
      }
    },
  },
  extraReducers: builder => {
    builder
      // Bulk update allergen assignments
      .addCase(bulkUpdateAllergenAssignments.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(bulkUpdateAllergenAssignments.fulfilled, state => {
        state.isLoading = false
        state.lastUpdated = Date.now()
      })
      .addCase(bulkUpdateAllergenAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Get allergen assignments
      .addCase(getAllergenAssignments.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAllergenAssignments.fulfilled, (state, action) => {
        state.isLoading = false
        const { data } = action.payload
        state.assignments = data.assignments || []
        state.pagination = data.pagination || state.pagination
        state.lastUpdated = Date.now()
      })
      .addCase(getAllergenAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Get item allergen assignments
      .addCase(getItemAllergenAssignments.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getItemAllergenAssignments.fulfilled, (state, action) => {
        state.isLoading = false
        const { data } = action.payload
        // Update assignments for this specific item
        const { itemType, itemId } = action.meta.arg
        state.assignments = state.assignments.filter(
          assignment =>
            !(assignment.itemType === itemType && assignment.itemId === itemId),
        )
        state.assignments.push(...(data.assignments || []))
        state.lastUpdated = Date.now()
      })
      .addCase(getItemAllergenAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Delete item allergen assignments
      .addCase(deleteItemAllergenAssignments.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteItemAllergenAssignments.fulfilled, (state, action) => {
        state.isLoading = false
        const { itemType, itemId } = action.meta.arg
        state.assignments = state.assignments.filter(
          assignment =>
            !(assignment.itemType === itemType && assignment.itemId === itemId),
        )
        state.lastUpdated = Date.now()
      })
      .addCase(deleteItemAllergenAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
  },
})

export const {
  clearError,
  setLastUpdated,
  enableRealTime,
  disableRealTime,
  addRealTimeUpdate,
} = unifiedAllergenSlice.actions

export default unifiedAllergenSlice.reducer
